import { Observable, ReplaySubject } from 'rxjs'
import { initRegionalContent } from './regional-content'

const RegionServiceLocalStorageKey = 'gmwp.region_select_cookie'
const CustomerLocationLocalStorageKey = 'gmwp.cookie_customer_location'

export interface Regions {
	[key: string]: {
		label: string
		default?: true
	}
} // Example: { TX: { label: 'Texas'; default: true }, FL: { label: 'Florida' }, ... }
export type Region = { code: string; name: string; default?: true } // Example: { code: 'TX', name: 'Texas' }

export type LocalStorageCustomerLocation = {
	cities: string[]
	state: string
	zipcode: string
	latitude: string
	longitude: string
}

interface LocalStorageRegion {
	regionCode: string
}

export class RegionService {
	private _availableRegions: Regions
	private _currentRegion$: ReplaySubject<Region> = new ReplaySubject()
	public _initialized = false

	//
	// Private
	//

	// Methods

	private _checkLocalStorageForRegion(): Region | false {
		const storedRegion = localStorage.getItem(RegionServiceLocalStorageKey)

		if (storedRegion) {
			try {
				const region = this._convertLocalStorageRegionToRegion(JSON.parse(storedRegion))
				return region
			} catch {
				return false
			}
		} else {
			return false
		}
	}

	//"{\"cities\":[],\"state\":\"ON\",\"zipcode\":\"\",\"latitude\":\"\",\"longitude\":\"\"}"
	private _convertRegionToCustomerCookie(region: Region): LocalStorageCustomerLocation {
		const cities = [],
			state = region.code,
			zipcode = '',
			latitude = '',
			longitude = ''
		return {
			cities: cities,
			state: state,
			zipcode: zipcode,
			latitude: latitude,
			longitude: longitude,
		}
	}

	private _convertRegionToLocalStorageRegion(region: Region): LocalStorageRegion {
		return {
			regionCode: region.code,
		}
	}

	private _convertLocalStorageRegionToRegion(region: LocalStorageRegion): Region {
		const regionLabel = this._availableRegions[region.regionCode]?.label

		return {
			code: region.regionCode,
			name: regionLabel,
		}
	}

	public get isRegionCookieAvailable(): boolean {
		return localStorage.getItem(RegionServiceLocalStorageKey) ? true : false
	}

	public get isCustomerCookieAvailable(): boolean {
		return localStorage.getItem(CustomerLocationLocalStorageKey) ? true : false
	}

	private _getDefaultRegion(): Region {
		const [key, region] = Object.entries(this._availableRegions).find(([, region]) => region.default)
		return { code: key, name: region.label }
	}

	private _setLocalStorage(region: Region): void {
		const localStorageRegion = this._convertRegionToLocalStorageRegion(region)

		localStorage.setItem(RegionServiceLocalStorageKey, JSON.stringify(localStorageRegion))
		this._setCustomerLocationCookie(region)
	}

	private _setRegion(region: Region, setLocalStorage = true): void {
		this._currentRegion$.next(region)
		if (setLocalStorage) this._setLocalStorage(region)
		// TODO: Remove this once we migrate away from legacy quantum components.
		// This allows quantum components to react to region changes from our code.
		document.dispatchEvent(
			new CustomEvent('gbds-selectable-list-event', {
				detail: {
					...region, // Spreads to: { code, name }
				},
			})
		)
	}

	private _setCustomerLocationCookie(region: Region) {
		const localStorageCustomerLocation = this._convertRegionToCustomerCookie(region)
		// Adding a second (not ideal) stringify so the cookie is in the format that it is expected to be in for other components.
		// Refer province-selector-dao.ts in gbds-quantum
		localStorage.setItem(
			CustomerLocationLocalStorageKey,
			JSON.stringify(JSON.stringify(localStorageCustomerLocation))
		)
	}

	//
	// Public
	//

	// Methods

	/**
	 * Initialize the region service; if called more than once, subsequent calls do nothing.
	 * @param regions regions object to set as the available regions to choose from.
	 */
	public init(regions: Regions): void {
		if (!this._initialized) {
			this._availableRegions = regions
			const localStorageRegion = this._checkLocalStorageForRegion()

			if (localStorageRegion) {
				this._setRegion(localStorageRegion)
			} else {
				const defaultRegion = this._getDefaultRegion()
				// Don't set localstorage for default region, only when user interacts and changes region
				this._setRegion(defaultRegion, false)
			}

			this._initialized = true

			initRegionalContent()
		}
	}

	/**
	 * Sets the region.
	 * Propagates new region to subscribers of currentRegion$ and sets the region in localstorage.
	 * @param regionKey the two character code region to set as the region.
	 * @returns `true` if the region was set successfully, `false`s otherwise.
	 */
	public setRegion(regionKey: string): boolean {
		const regionLabel = this._availableRegions[regionKey]?.label

		if (regionLabel) {
			const region = { code: regionKey, name: regionLabel }
			this._setRegion(region)
			return true
		} else {
			return false
		}
	}

	// Accessors

	/**
	 * Observable of Region. Can be subscribed to receive new data when the region changes.
	 *
	 * **Note:** you *must* store the subscriber and call `unsubscribe` on it in teardown logic to prevent memory leaks.
	 *
	 * @example Custom element example:
	 * ```
	 * class SomeClass extends HTMLElement {
	 * 	constructor() {
	 * 		super()
	 * 	}
	 *
	 * 	connectedCallback() {
	 * 		this.subscription = window.gbpe.regionService.currentRegion$.subscribe()
	 * 	}
	 *
	 *  disconnectedCallback() {
	 * 		this.subscription.unsubscribe()
	 *  }
	 * }
	 * ```
	 */
	public get currentRegion$(): Observable<Region> {
		return this._currentRegion$.asObservable()
	}

	/**
	 * The available regions.
	 *
	 * Returns undefined if the service hasn't initialized.
	 */
	public get availableRegions(): Regions | undefined {
		if (this._initialized) {
			return this._availableRegions
		} else {
			return undefined
		}
	}
}
